import classNames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { OnlineModelCategory } from '../../../../generated/api/onlineModelCategory';
import { DeviceAddonsView, useHasDeviceAddons } from './DeviceAddonsView';
import { SubscriptionsAddonsView, useHasSubscriptionAddons } from './SubscriptionsAddonsView';
import type { DeviceAddOnTogglePayload, EnrollmentProgramTogglePayload } from './DeviceAddonsView';
import type { BasketItem } from '../../../../common/types/shoppingBasket';
import type { ChildOrderItem } from '../../../../generated/api/childOrderItem';
import type { CompanyInfoResponse } from '../../../../generated/api/companyInfoResponse';
import type { Offer } from '../../../../generated/api/offer';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type { AuthenticatedUser } from '../../../../public/site/authState';
import type { PlaceOrderFormData, PlaceOrderItemUnit } from '../../placeOrderTypes';

import './AddOnSelection.scss';

export interface AddOnSelectionProps {
  itemIndex: number;
  orderItem: PlaceOrderItemUnit;
  onlineModel: OnlineModel;
  basketItem: BasketItem;
  mdmId: string;
  offer: Offer;
  user: AuthenticatedUser;
  companyInfo: CompanyInfoResponse;
  updateAddons: (
    basketItemId: string,
    productIndex: number,
    addons: string[],
    addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
  ) => void;
}

export const AddOnSelection = ({
  itemIndex,
  orderItem,
  onlineModel,
  basketItem,
  mdmId,
  offer,
  user,
  companyInfo,
  updateAddons,
}: AddOnSelectionProps) => {
  const { getValues, setValue } = useFormContext<PlaceOrderFormData>();

  const showSubscriptionsAddons = onlineModel.category === OnlineModelCategory.SELF_SERVICE;
  const showDeviceAddons = ![OnlineModelCategory.SALES_PRODUCT, OnlineModelCategory.SELF_SERVICE].includes(
    onlineModel.category as OnlineModelCategory
  );

  const { hasSubscriptionAddons } = useHasSubscriptionAddons({
    commercialProductCode: basketItem.offer.commercialProductGuid,
    mdmId,
  });

  const hasDeviceAddons = useHasDeviceAddons({
    basketItem,
    onlineModel,
    offer,
    user,
    companyInfo,
  });

  const updateOrderItemUnit = (updater: (unit: PlaceOrderItemUnit) => PlaceOrderItemUnit) => {
    const currentUnits = getValues('products.placeOrderItemUnits') ?? [];
    const targetIndex = currentUnits.findIndex(
      unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
    );

    if (targetIndex === -1) {
      return;
    }

    const nextUnits = [...currentUnits];
    nextUnits[targetIndex] = updater(currentUnits[targetIndex]);

    setValue('products.placeOrderItemUnits', nextUnits, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    // Extract addOnAttributes from ChildOrderItems for basket persistence
    const addonAttributes = nextUnits[targetIndex].addons.reduce<{ [addOnCode: string]: { [key: string]: string } }>(
      (acc, addon) => {
        if (addon.addOnAttributes && Object.keys(addon.addOnAttributes).length > 0) {
          acc[addon.addOnCode] = addon.addOnAttributes;
        }
        return acc;
      },
      {}
    );

    updateAddons(
      orderItem.basketItemId,
      itemIndex,
      nextUnits[targetIndex].addons.map(addon => addon.addOnCode),
      Object.keys(addonAttributes).length > 0 ? addonAttributes : undefined
    );
  };

  const handleDeviceAddOnToggle = ({ addOnCode, addOnAssociationCode, isChecked }: DeviceAddOnTogglePayload) => {
    updateOrderItemUnit(unit => {
      const addons = isChecked
        ? unit.addons.some(addon => addon.addOnCode === addOnCode)
          ? unit.addons
          : [...unit.addons, { addOnCode, addOnAssociationCode }]
        : unit.addons.filter(addon => addon.addOnCode !== addOnCode);

      return { ...unit, addons };
    });
  };

  const handleEnrollmentProgramToggle = ({ programType, isSelected, alias }: EnrollmentProgramTogglePayload) => {
    updateOrderItemUnit(unit => ({
      ...unit,
      enrollmentProgram: isSelected ? programType : undefined,
      enrollmentProgramAlias: alias,
    }));
  };

  const handleSubscriptionAddOnsChange = (nextAddOns: ChildOrderItem[]) => {
    updateOrderItemUnit(unit => ({
      ...unit,
      addons: nextAddOns,
    }));
  };

  const shouldShowSubscriptionsAddons = showSubscriptionsAddons && hasSubscriptionAddons;
  const shouldShowDeviceAddons = showDeviceAddons && hasDeviceAddons;

  if (!shouldShowSubscriptionsAddons && !shouldShowDeviceAddons) {
    return null;
  }

  return (
    <div
      className={classNames(
        'of-order-item-units-addon-selection',
        dsClass.PADDING_HORIZONTAL_4,
        dsClass.PADDING_TOP_2,
        dsClass.PADDING_BOTTOM_4
      )}
    >
      {shouldShowSubscriptionsAddons && (
        <SubscriptionsAddonsView
          mdmId={mdmId}
          commercialProductCode={basketItem.offer.commercialProductGuid}
          onlineModel={onlineModel}
          itemInstanceId={`${basketItem.id}-${itemIndex}`}
          orderItem={orderItem}
          onAddOnsUpdate={handleSubscriptionAddOnsChange}
        />
      )}
      {shouldShowDeviceAddons && (
        <DeviceAddonsView
          basketItem={basketItem}
          onlineModel={onlineModel}
          offer={offer}
          user={user}
          companyInfo={companyInfo}
          orderItem={orderItem}
          onAddOnToggle={handleDeviceAddOnToggle}
          onEnrollmentProgramToggle={handleEnrollmentProgramToggle}
        />
      )}
    </div>
  );
};
